import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaTimes,
  FaTimesCircle,
} from 'react-icons/fa';

const typeStyles = {
  success: {
    surface: 'bg-emerald-50',
    accent: 'bg-emerald-400',
    border: 'border-emerald-200',
    iconBg: 'bg-emerald-100',
    iconText: 'text-emerald-600',
    progress: 'bg-emerald-400',
    icon: <FaCheckCircle />,
    defaultTitle: 'Success',
  },
  info: {
    surface: 'bg-sky-50',
    accent: 'bg-sky-400',
    border: 'border-sky-200',
    iconBg: 'bg-sky-100',
    iconText: 'text-sky-600',
    progress: 'bg-sky-400',
    icon: <FaInfoCircle />,
    defaultTitle: 'Heads up',
  },
  warning: {
    surface: 'bg-amber-50',
    accent: 'bg-amber-400',
    border: 'border-amber-200',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-600',
    progress: 'bg-amber-400',
    icon: <FaExclamationTriangle />,
    defaultTitle: 'Needs attention',
  },
  error: {
    surface: 'bg-rose-50',
    accent: 'bg-rose-400',
    border: 'border-rose-200',
    iconBg: 'bg-rose-100',
    iconText: 'text-rose-600',
    progress: 'bg-rose-400',
    icon: <FaTimesCircle />,
    defaultTitle: 'Something went wrong',
  },
};

function AlertMessage({ type = 'info', title, message, onClose, timeout = 5000 }) {
  const styles = typeStyles[type] || typeStyles.info;
  const displayTitle = title || styles.defaultTitle;
  const shouldAutoClose = Boolean(onClose && timeout > 0);

  useEffect(() => {
    if (!shouldAutoClose) {
      return undefined;
    }

    const timer = setTimeout(onClose, timeout);
    return () => clearTimeout(timer);
  }, [onClose, shouldAutoClose, timeout]);

  return createPortal(
    <div
      className={`fixed right-4 top-4 z-[10050] w-[calc(100vw-2rem)] max-w-md overflow-hidden rounded-xl border ${styles.border} ${styles.surface} text-slate-900 shadow-xl shadow-slate-900/10 ring-1 ring-slate-900/5 animate-[alertSlideIn_180ms_ease-out] sm:right-6 sm:top-6`}
      role={type === 'error' ? 'alert' : 'status'}
      aria-live={type === 'error' ? 'assertive' : 'polite'}
    >
      <div className={`absolute inset-y-0 left-0 w-1 ${styles.accent}`} aria-hidden="true" />
      <div className="flex items-start gap-3 p-4 pl-5">
        <div className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${styles.iconBg}`}>
          <span className={`text-xl ${styles.iconText}`} aria-hidden="true">
            {styles.icon}
          </span>
        </div>

        <div className="min-w-0 flex-1 pr-1">
          <div className="text-sm font-semibold leading-5 text-slate-900">{displayTitle}</div>
          {message && <div className="mt-1 text-sm leading-5 text-slate-600">{message}</div>}
        </div>

        {onClose && (
          <button
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-900/5 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
            onClick={onClose}
            aria-label="Dismiss notification"
            type="button"
          >
            <FaTimes aria-hidden="true" />
          </button>
        )}
      </div>

      {shouldAutoClose && (
        <div
          className={`h-1 origin-left ${styles.progress} animate-[alertProgress_linear_forwards]`}
          style={{ animationDuration: `${timeout}ms` }}
          aria-hidden="true"
        />
      )}
    </div>,
    document.body
  );
}

export default AlertMessage;
